// ===================================
// VOICE INTERVIEW COMPONENT - STAGE 8 FIXED
// src/components/VoiceInterview.js
// Supports both VAPI mode and Browser voice + Claude
// FIX: Uses startInterview for greeting, proper speech synthesis
// ===================================

import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { startWebCall, endWebCall, updateCallStatus } from '../services/voiceApi';
import { startInterview, sendMessage } from '../services/interviewApi';
import './VoiceInterview.css';

// Try to import VAPI SDK (optional - only needed if VAPI mode is enabled)
let VapiSDK = null;
try {
  VapiSDK = require('@vapi-ai/web').default;
} catch (e) {
  console.log('VAPI SDK not installed - browser voice mode only');
}

const VoiceInterview = ({ session, onEnd }) => {
  const navigate = useNavigate();
  
  // Mode: 'browser' or 'vapi'
  const [voiceMode, setVoiceMode] = useState(null);
  
  // Call state
  const [callState, setCallState] = useState('idle');
  const [callId, setCallId] = useState(null);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // Timer state
  const [remainingTime, setRemainingTime] = useState(session?.duration * 60 || 1800);
  const [timerWarning, setTimerWarning] = useState(false);
  
  // Audio visualization
  const [volume, setVolume] = useState(0);
  const [isMuted, setIsMuted] = useState(false);
  
  // Transcript state
  const [liveTranscript, setLiveTranscript] = useState([]);
  const [currentSpeech, setCurrentSpeech] = useState('');
  const [isAiThinking, setIsAiThinking] = useState(false);
  
  // Speech synthesis state
  const [voicesLoaded, setVoicesLoaded] = useState(false);
  
  // Refs
  const vapiRef = useRef(null);
  const timerRef = useRef(null);
  const recognitionRef = useRef(null);
  const conversationRef = useRef([]); // Keep track of full conversation for API calls

  // Load speech synthesis voices
  useEffect(() => {
    if ('speechSynthesis' in window) {
      const loadVoices = () => {
        const voices = window.speechSynthesis.getVoices();
        if (voices.length > 0) {
          setVoicesLoaded(true);
          console.log('🔊 Speech voices loaded:', voices.length);
        }
      };
      
      // Load immediately if available
      loadVoices();
      
      // Also listen for voiceschanged event
      window.speechSynthesis.onvoiceschanged = loadVoices;
    }
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
      if (vapiRef.current?.stop) {
        try { vapiRef.current.stop(); } catch (e) {}
      }
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);

  // Timer countdown
  useEffect(() => {
    if (callState === 'connected' || callState === 'speaking' || callState === 'listening') {
      timerRef.current = setInterval(() => {
        setRemainingTime(prev => {
          const newTime = prev - 1;
          if (newTime <= 60 && !timerWarning) {
            setTimerWarning(true);
          }
          if (newTime <= 0) {
            handleEndCall();
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [callState, timerWarning]);

  // Format time
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // ===================================
  // START CALL - Determines mode from backend
  // ===================================
const handleStartCall = async () => {
  try {
    setIsLoading(true);
    setError(null);
    setCallState('connecting');
    
    // Call backend - returns mode + assistantConfig (for VAPI) or just mode (for browser)
    const response = await startWebCall(session.id);
    
    if (!response.success) {
      throw new Error(response.message || 'Failed to start call');
    }

    console.log('🎙️ Voice mode:', response.mode);
    console.log('🔑 Has VapiSDK:', !!VapiSDK);
    console.log('📋 Has assistantConfig:', !!response.assistantConfig);
    
    setVoiceMode(response.mode);

    // Check if we should use VAPI
    if (response.mode === 'vapi' && VapiSDK && response.assistantConfig) {
      console.log('✅ Using VAPI mode with 11Labs voice');
      await initializeVapiClient(response.assistantConfig);
    } else {
      // Browser mode (default fallback)
      console.log('⚠️ Using browser voice mode. Reasons:', {
        mode: response.mode,
        hasVapiSDK: !!VapiSDK,
        hasAssistantConfig: !!response.assistantConfig
      });
      await initializeBrowserVoice();
    }

    setCallState('connected');
    updateCallStatus(session.id, 'connected', 'call_started');

  } catch (err) {
    console.error('Start call error:', err);
    setError(err.message || 'Failed to start voice call');
    setCallState('error');
  } finally {
    setIsLoading(false);
  }
};

  // ===================================
  // VAPI MODE - Initialize VAPI SDK
  // ===================================
const initializeVapiClient = async (assistantConfig) => {
  try {
    console.log('🔑 Initializing VAPI with assistantConfig');
    console.log('   Voice provider:', assistantConfig.voice?.provider);
    console.log('   Voice ID:', assistantConfig.voice?.voiceId);
    
    const vapiPublicKey = process.env.REACT_APP_VAPI_PUBLIC_KEY;
    
    if (!vapiPublicKey) {
      throw new Error('VAPI public key not configured');
    }
    
    const vapi = new VapiSDK(vapiPublicKey);
    vapiRef.current = vapi;
    
    vapi.on('call-start', () => {
      console.log('📞 VAPI call started - 11Labs voice active!');
      setCallState('connected');
    });

    vapi.on('call-end', () => {
      console.log('📞 VAPI call ended');
      setCallState('ended');
      handleEndCall();
    });

    vapi.on('speech-start', () => {
      console.log('🗣️ AI speaking (11Labs)');
      setCallState('speaking');
    });
    
    vapi.on('speech-end', () => {
      console.log('👂 Listening for user');
      setCallState('listening');
    });

    // FIX: Update BOTH liveTranscript AND conversationRef
    vapi.on('message', (message) => {
      console.log('📨 VAPI message:', message.type);
      if (message.type === 'transcript') {
        const newMessage = {
          role: message.role,
          content: message.transcript,
          timestamp: new Date().toISOString()
        };
        setLiveTranscript(prev => [...prev, newMessage]);
        // FIX: Also update conversationRef so transcript is sent on end!
        conversationRef.current = [...conversationRef.current, newMessage];
        console.log('📝 Transcript updated, total messages:', conversationRef.current.length);
      }
    });

    vapi.on('volume-level', (level) => setVolume(level));

    vapi.on('error', (error) => {
      console.error('❌ VAPI error:', error);
      setError(error.message || 'VAPI connection error');
      setCallState('error');
    });

    console.log('🚀 Starting VAPI call with assistant config...');
    const call = await vapi.start(assistantConfig);
    
    console.log('✅ VAPI call created:', call?.id);
    
    if (call?.id) {
      setCallId(call.id);
      updateCallStatus(session.id, 'connecting', 'vapi_call_created', call.id);
    }

  } catch (err) {
    console.error('❌ VAPI init error:', err);
    setError('Voice connection failed: ' + (err.message || 'Unknown error'));
    setCallState('error');
  }
};

  // ===================================
  // BROWSER MODE - Speech Recognition + Claude
  // ===================================
  const initializeBrowserVoice = async () => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      throw new Error('Voice recognition not supported. Please use Chrome or Edge.');
    }

    console.log('🎤 Initializing browser voice mode');

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    recognition.onresult = async (event) => {
      let finalTranscript = '';
      let interimTranscript = '';
      
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }
      
      setCurrentSpeech(interimTranscript);
      
      if (finalTranscript && finalTranscript.trim().length > 0) {
        // Add user message to transcript
        const userMessage = {
          role: 'user',
          content: finalTranscript.trim(),
          timestamp: new Date().toISOString()
        };
        setLiveTranscript(prev => [...prev, userMessage]);
        conversationRef.current = [...conversationRef.current, userMessage];
        setCurrentSpeech('');
        
        // Send to Claude and get response
        await sendToClaudeAndSpeak(finalTranscript.trim());
      }
    };

    recognition.onerror = (event) => {
      console.error('Speech recognition error:', event.error);
      if (event.error !== 'no-speech' && event.error !== 'aborted') {
        setError(`Speech error: ${event.error}`);
      }
    };

    recognition.onend = () => {
      // Auto-restart if still in call and not speaking
      if ((callState === 'connected' || callState === 'listening') && !isMuted) {
        setTimeout(() => {
          try { 
            if (recognitionRef.current) {
              recognitionRef.current.start(); 
            }
          } catch (e) {
            console.log('Could not restart recognition:', e.message);
          }
        }, 100);
      }
    };

    // Start recognition
    try {
      recognition.start();
      console.log('🎤 Speech recognition started');
    } catch (e) {
      console.error('Failed to start recognition:', e);
    }
    
    // Generate initial AI greeting using startInterview API
    await generateInitialGreeting();
  };

  // Generate initial greeting from Claude using startInterview
  const generateInitialGreeting = async () => {
    try {
      setIsAiThinking(true);
      setCallState('speaking');
      
      console.log('🎯 Calling startInterview API for session:', session.id);
      
      // Clear any stale transcript data
      setLiveTranscript([]);
      conversationRef.current = [];
      
      // Use the startInterview API (same as text chat)
      const response = await startInterview(session.id);
      
      console.log('📨 startInterview response:', response);
      
      if (response.initialMessage) {
        const aiMessage = {
          role: 'assistant',
          content: response.initialMessage,
          timestamp: new Date().toISOString()
        };
        setLiveTranscript([aiMessage]);
        conversationRef.current = [aiMessage];
        
        console.log('🔊 Speaking initial greeting:', response.initialMessage.substring(0, 50) + '...');
        await speakText(response.initialMessage);
      } else if (response.existingTranscript && response.existingTranscript.length > 0) {
        // Resuming existing conversation
        console.log('♻️ Resuming conversation with', response.existingTranscript.length, 'messages');
        
        // Filter out any stale __START_INTERVIEW__ messages
        const cleanTranscript = response.existingTranscript.filter(
          msg => !msg.content?.includes('__START_INTERVIEW__')
        );
        
        setLiveTranscript(cleanTranscript);
        conversationRef.current = cleanTranscript;
        
        // Speak the last AI message
        const lastAiMessage = [...response.existingTranscript].reverse().find(m => m.role === 'assistant');
        if (lastAiMessage) {
          await speakText("Welcome back! Let's continue where we left off. " + lastAiMessage.content);
        }
      }
    } catch (err) {
      console.error('Error getting initial greeting:', err);
      // Fallback greeting
      const fallbackGreeting = `Hello and welcome to your ${session.duration}-minute practice interview. I'll be your ${session.interviewer_personality} interviewer today. Let's begin - please tell me about yourself.`;
      const aiMessage = {
        role: 'assistant',
        content: fallbackGreeting,
        timestamp: new Date().toISOString()
      };
      setLiveTranscript([aiMessage]);
      conversationRef.current = [aiMessage];
      await speakText(fallbackGreeting);
    } finally {
      setIsAiThinking(false);
    }
  };

  // Send user speech to Claude and speak the response
  const sendToClaudeAndSpeak = async (userText) => {
    // Check if session has expired before sending
    if (remainingTime <= 0) {
      console.log('⏰ Session time expired, cannot send message');
      setError('Session time has expired. Please end the interview.');
      setCallState('ended');
      return;
    }
    
    try {
      setIsAiThinking(true);
      setCallState('speaking');
      
      // Pause recognition while AI is responding
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
      
      console.log('📤 Sending to Claude:', userText.substring(0, 50) + '...');
      console.log('📤 Session ID:', session.id);
      console.log('📤 Conversation history length:', conversationRef.current.length);
      
      // Filter out any stale __START_INTERVIEW__ messages from history
      const cleanHistory = conversationRef.current.filter(
        msg => !msg.content.includes('__START_INTERVIEW__')
      );
      
      // Send to backend using sendMessage API with conversation history
      const response = await sendMessage(session.id, userText, cleanHistory);
      
      console.log('📨 Claude response:', response);
      
      if (response.response) {
        const aiMessage = {
          role: 'assistant',
          content: response.response,
          timestamp: new Date().toISOString()
        };
        setLiveTranscript(prev => [...prev, aiMessage]);
        conversationRef.current = [...conversationRef.current, aiMessage];
        
        console.log('🔊 Speaking AI response:', response.response.substring(0, 50) + '...');
        
        // Speak the response
        await speakText(response.response);
      } else {
        throw new Error('No response from AI');
      }
    } catch (err) {
      console.error('Error sending to Claude:', err);
      
      // Extract the actual error message from various sources
      let errorMessage = 'Unknown error';
      if (err.response) {
        // Server responded with error status
        console.error('Backend response status:', err.response.status);
        console.error('Backend response data:', err.response.data);
        errorMessage = err.response.data?.message || 
                       err.response.data?.error || 
                       JSON.stringify(err.response.data) ||
                       `Server error: ${err.response.status}`;
      } else if (err.request) {
        // Request was made but no response
        errorMessage = 'No response from server';
      } else {
        // Error setting up request
        errorMessage = err.message;
      }
      
      console.error('Final error message:', errorMessage);
      console.error('Session state:', { id: session.id, status: session.status, interview_type: session.interview_type });
      
      setError('AI Error: ' + errorMessage);
      setCallState('listening');
      
      // Restart recognition
      if (recognitionRef.current) {
        try { recognitionRef.current.start(); } catch (e) {}
      }
    } finally {
      setIsAiThinking(false);
    }
  };

  // Text to speech - returns a promise that resolves when speech is done
  const speakText = (text) => {
    return new Promise((resolve) => {
      if (!('speechSynthesis' in window)) {
        console.error('Speech synthesis not supported');
        setCallState('listening');
        resolve();
        return;
      }

      // Cancel any ongoing speech
      window.speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.rate = 0.95;
      utterance.pitch = 1;
      utterance.volume = 1;
      
      // Try to get a better voice
      const voices = window.speechSynthesis.getVoices();
      const englishVoices = voices.filter(v => v.lang.startsWith('en'));
      if (englishVoices.length > 0) {
        // Prefer Google or Microsoft voices
        const preferredVoice = englishVoices.find(v => 
          v.name.includes('Google') || v.name.includes('Microsoft') || v.name.includes('Samantha')
        ) || englishVoices[0];
        utterance.voice = preferredVoice;
        console.log('🔊 Using voice:', preferredVoice.name);
      }
      
      utterance.onstart = () => {
        console.log('🔊 Speech started');
        setCallState('speaking');
        // Pause recognition while speaking
        if (recognitionRef.current) {
          try { recognitionRef.current.stop(); } catch (e) {}
        }
      };
      
      utterance.onend = () => {
        console.log('🔊 Speech ended');
        setCallState('listening');
        // Resume recognition after speaking
        if (recognitionRef.current && callState !== 'ended' && !isMuted) {
          setTimeout(() => {
            try { recognitionRef.current.start(); } catch (e) {
              console.log('Could not restart recognition:', e.message);
            }
          }, 200);
        }
        resolve();
      };
      
      utterance.onerror = (event) => {
        console.error('Speech synthesis error:', event);
        setCallState('listening');
        if (recognitionRef.current && !isMuted) {
          try { recognitionRef.current.start(); } catch (e) {}
        }
        resolve();
      };
      
      console.log('🔊 Starting speech synthesis...');
      window.speechSynthesis.speak(utterance);
      
      // Safety timeout - if speech doesn't end in 60 seconds, resolve anyway
      setTimeout(() => {
        resolve();
      }, 60000);
    });
  };

  // ===================================
  // END CALL
  // ===================================
  const handleEndCall = async () => {
    try {
      setIsLoading(true);
      
      // Stop timer
      if (timerRef.current) clearInterval(timerRef.current);
      
      // Stop speech recognition
      if (recognitionRef.current) {
        try { recognitionRef.current.stop(); } catch (e) {}
      }
      
      // Stop speech synthesis
      if ('speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
      
      // Stop VAPI if active
      if (vapiRef.current?.stop) {
        try { vapiRef.current.stop(); } catch (e) {}
      }

      // Notify backend with transcript
      const result = await endWebCall(session.id, callId, conversationRef.current);
      console.log('📞 Call ended:', result);

      setCallState('ended');
      
      // Callback to parent
      if (onEnd) {
        onEnd({
          transcript: conversationRef.current,
          evaluation: result.evaluation
        });
      }

      // Navigate to results after a moment
      setTimeout(() => {
        navigate(`/app/sessions/${session.id}`);
      }, 2000);

    } catch (err) {
      console.error('End call error:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Toggle mute
  const toggleMute = () => {
    setIsMuted(prev => {
      const newMuted = !prev;
      if (recognitionRef.current) {
        if (newMuted) {
          try { recognitionRef.current.stop(); } catch (e) {}
        } else {
          try { recognitionRef.current.start(); } catch (e) {}
        }
      }
      if (vapiRef.current?.setMuted) {
        vapiRef.current.setMuted(newMuted);
      }
      return newMuted;
    });
  };

  // Get status display
  const getStatusDisplay = () => {
    switch (callState) {
      case 'idle':
        return { text: 'Ready to Start', color: 'neutral', icon: '🎙️' };
      case 'connecting':
        return { text: 'Connecting...', color: 'warning', icon: '📡' };
      case 'connected':
        return { text: 'Connected', color: 'success', icon: '🟢' };
      case 'speaking':
        return { text: isAiThinking ? 'AI Thinking...' : 'AI Speaking', color: 'info', icon: '🔊' };
      case 'listening':
        return { text: 'Listening...', color: 'primary', icon: '👂' };
      case 'ended':
        return { text: 'Call Ended', color: 'neutral', icon: '📴' };
      case 'error':
        return { text: 'Error', color: 'error', icon: '❌' };
      default:
        return { text: 'Unknown', color: 'neutral', icon: '❓' };
    }
  };

  const status = getStatusDisplay();

  return (
    <div className="voice-interview">
      {/* Header */}
      <div className="voice-header">
        <div className="voice-info">
          <h2>🎙️ Voice Interview</h2>
          <p>
            {session?.interviewer_personality} • {session?.experience_level} • {session?.duration} min
            {/* {voiceMode && <span className="mode-badge">{voiceMode === 'vapi' ? '🌐 VAPI' : '🖥️ Browser'}</span>} */}
          </p>
        </div>
        
        {(callState === 'connected' || callState === 'speaking' || callState === 'listening') && (
          <div className={`voice-timer ${timerWarning ? 'warning' : ''}`}>
            <span className="timer-icon">⏱️</span>
            <span className="timer-value">{formatTime(remainingTime)}</span>
          </div>
        )}
      </div>

      {/* Error display */}
      {error && (
        <div className="voice-error">
          <span className="error-icon">⚠️</span>
          <span>{error}</span>
          <button onClick={() => setError(null)} className="error-close">×</button>
        </div>
      )}

      {/* Main content */}
      <div className="voice-content">
        {/* Status indicator */}
        <div className={`call-status status-${status.color}`}>
          <span className="status-icon">{status.icon}</span>
          <span className="status-text">{status.text}</span>
        </div>

        {/* Audio visualization */}
        {(callState === 'connected' || callState === 'speaking' || callState === 'listening') && (
          <div className="audio-visualizer">
            <div className="visualizer-bars">
              {[...Array(20)].map((_, i) => (
                <div 
                  key={i} 
                  className={`bar ${callState === 'speaking' ? 'speaking' : callState === 'listening' ? 'listening' : ''}`}
                  style={{ 
                    animationDelay: `${i * 0.05}s`
                  }}
                />
              ))}
            </div>
            <div className="visualizer-label">
              {callState === 'speaking' ? (isAiThinking ? 'AI is thinking...' : 'AI Speaking...') : 
               callState === 'listening' ? 'Your turn to speak' : 'Ready'}
            </div>
          </div>
        )}

        {/* Idle state - Start button */}
        {callState === 'idle' && (
          <div className="voice-idle">
            <div className="idle-icon">🎤</div>
            <h3>Ready for Voice Interview</h3>
            <p>Click the button below to start your voice-based interview.</p>
            <div className="idle-tips">
              <h4>Quick Tips:</h4>
              <ul>
                <li>Find a quiet environment</li>
                <li>Speak clearly and at a normal pace</li>
                <li>Allow microphone access when prompted</li>
                <li>Wait for the AI to finish speaking before responding</li>
              </ul>
            </div>
            {!voicesLoaded && (
              <p className="voice-warning">⚠️ Loading speech voices...</p>
            )}
            <button 
              onClick={handleStartCall} 
              className="btn btn-primary btn-lg start-call-btn"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <span className="spinner"></span>
                  Starting...
                </>
              ) : (
                <>
                  <span>🎙️</span>
                  Start Voice Interview
                </>
              )}
            </button>
          </div>
        )}

        {/* Live transcript */}
        {(callState !== 'idle' && callState !== 'ended') && (
          <div className="live-transcript">
            <h4>📝 Live Transcript</h4>
            <div className="transcript-messages">
              {liveTranscript.map((msg, index) => (
                <div key={index} className={`transcript-msg ${msg.role}`}>
                  <span className="msg-role">{msg.role === 'assistant' ? '🎯 AI' : '👤 You'}</span>
                  <span className="msg-text">{msg.content}</span>
                </div>
              ))}
              {currentSpeech && (
                <div className="transcript-msg user interim">
                  <span className="msg-role">👤 You</span>
                  <span className="msg-text">{currentSpeech}...</span>
                </div>
              )}
              {isAiThinking && (
                <div className="transcript-msg assistant thinking">
                  <span className="msg-role">🎯 AI</span>
                  <span className="msg-text">
                    <span className="thinking-dots">
                      <span>.</span><span>.</span><span>.</span>
                    </span>
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Call ended state */}
        {callState === 'ended' && (
          <div className="voice-ended">
            <div className="ended-icon">✅</div>
            <h3>Interview Complete</h3>
            <p>Processing your results...</p>
            <div className="spinner"></div>
          </div>
        )}
      </div>

      {/* Controls */}
      {(callState === 'connected' || callState === 'speaking' || callState === 'listening') && (
        <div className="voice-controls">
          <button 
            onClick={toggleMute} 
            className={`control-btn ${isMuted ? 'muted' : ''}`}
            title={isMuted ? 'Unmute' : 'Mute'}
          >
            {isMuted ? '🔇' : '🔊'}
          </button>
          
          <button 
            onClick={handleEndCall} 
            className="control-btn end-call"
            disabled={isLoading}
          >
            {isLoading ? '...' : '📴'} End Interview
          </button>
        </div>
      )}
    </div>
  );
};

export default VoiceInterview;